import { useState } from "react"
import { ChevronDown, Users } from "lucide-react"
import { cn } from "@/lib/utils"
import type { TachoCardCounters, TachoCardRow } from "@/lib/aiWidgetTachoParser"
import {
  drivingLight,
  formatClock,
  formatDay,
  formatMin,
  isDayDone,
  minutesToStop,
  movingRows,
  standingRows,
} from "./tachoCardFormat"
import {
  ACTIVITY_ICON,
  LIGHT_DOT,
  LIGHT_TEXT,
  TachoSourceFooter,
} from "./TachoCardParts"
import { useTachoCardStrings, type TachoCardStrings } from "./tachoCardStrings"

type Mode = "moving" | "standing"

const SHOWN = 5

const COUNTERS: Array<keyof TachoCardCounters> = [
  "breakMin",
  "todayMin",
  "weekMin",
  "twoWeeksMin",
]

function CounterValue({ value }: { value: number | null }) {
  return (
    <span className="font-medium tabular-nums">
      {value === null ? "–" : formatMin(value)}
    </span>
  )
}

function CrewSlots({ row }: { row: TachoCardRow }) {
  const t = useTachoCardStrings()
  return (
    <span className="flex w-5 flex-col gap-1 text-muted-foreground">
      {row.crew!.map((m) => {
        const Icon = ACTIVITY_ICON[m.activity]
        return (
          <span
            key={m.slot}
            title={`${t.driverSlot(m.slot)}: ${t.activities[m.activity]}`}
            className="flex items-center gap-0.5 text-[10px] font-semibold leading-none"
          >
            {m.slot}
            <Icon
              className={cn(
                "h-3.5 w-3.5",
                m.activity === "driving" && "text-foreground",
              )}
            />
          </span>
        )
      })}
    </span>
  )
}

function Details({ row }: { row: TachoCardRow }) {
  const t = useTachoCardStrings()
  if (row.crew) {
    return (
      <div className="mb-2 ml-11 mt-1 space-y-1 rounded-lg bg-muted/50 px-3 py-2 text-xs">
        {row.crew.map((m) => {
          const Icon = ACTIVITY_ICON[m.activity]
          return (
            <div
              key={m.slot}
              className="grid grid-cols-[170px_repeat(4,1fr)] items-center gap-3"
            >
              <span className="flex items-center gap-1.5 font-medium">
                <Icon className="h-3.5 w-3.5" />
                {t.crewMember(m.slot, t.activities[m.activity])}
              </span>
              {COUNTERS.map((key) => (
                <span key={key}>
                  <span className="text-muted-foreground">
                    {t.crewCounters[key]}{" "}
                  </span>
                  <CounterValue value={m.counters[key]} />
                </span>
              ))}
            </div>
          )
        })}
      </div>
    )
  }
  return (
    <div className="mb-2 ml-11 mt-1 grid grid-cols-2 gap-x-6 gap-y-1 rounded-lg bg-muted/50 px-3 py-2 text-xs sm:grid-cols-4">
      {COUNTERS.map((key) => (
        <div key={key}>
          <div className="text-muted-foreground">{t.rowCounters[key]}</div>
          <CounterValue value={row.counters[key]} />
        </div>
      ))}
    </div>
  )
}

function standingNote(row: TachoCardRow, t: TachoCardStrings) {
  if (row.crew) {
    return row.crew
      .map(
        (m) =>
          `${t.driverSlot(m.slot)}: ${m.remainingMin === null ? "–" : formatMin(m.remainingMin)}`,
      )
      .join(" · ")
  }
  if (isDayDone(row)) return t.dayDone
  if (row.remainingMin !== null && row.remainingMin < 60) {
    return t.onlyLeft(row.remainingMin)
  }
  return t.activities[row.activity]
}

function stopReasonText(row: TachoCardRow, t: TachoCardStrings) {
  if (row.crew && row.swapAt) return t.swap(formatClock(row.swapAt))
  return t.reasons[row.stopReason]
}

function TachoRow({ row, mode }: { row: TachoCardRow; mode: Mode }) {
  const t = useTachoCardStrings()
  const [open, setOpen] = useState(false)
  const moving = mode === "moving"
  const toStop = minutesToStop(row)
  const done = isDayDone(row)
  const light = done ? "off" : drivingLight(moving ? toStop : row.remainingMin)
  const Icon = ACTIVITY_ICON[row.activity]
  const label = t.activities[row.activity]
  return (
    <div className="border-t border-border first:border-t-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 py-2.5 text-left"
      >
        <span
          className={cn("h-2.5 w-2.5 shrink-0 rounded-full", LIGHT_DOT[light])}
        />
        {row.crew ? (
          <CrewSlots row={row} />
        ) : (
          <span title={label} className="w-5 text-muted-foreground">
            <Icon className="h-5 w-5" />
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="block text-sm">
            <span className="font-medium">{row.plate}</span>
            {row.crew && (
              <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 align-middle text-[11px] text-muted-foreground">
                <Users className="h-3 w-3" />
                {t.twoDrivers}
              </span>
            )}
            {row.name && (
              <span className="text-muted-foreground"> · {row.name}</span>
            )}
          </span>
          <span className="block text-xs text-muted-foreground">
            {moving ? stopReasonText(row, t) : standingNote(row, t)}
          </span>
        </span>
        <span className="text-right">
          <span
            className={cn(
              "block text-base font-semibold tabular-nums",
              LIGHT_TEXT[light],
            )}
          >
            {moving
              ? row.stopAt
                ? t.stop(formatClock(row.stopAt), !!row.crew)
                : "–"
              : done || row.remainingMin === null
                ? "–"
                : formatMin(row.remainingMin)}
          </span>
          <span className="block text-[11px] text-muted-foreground">
            {moving
              ? row.stopAt
                ? formatDay(row.stopAt, t.weekdays)
                : ""
              : done || row.remainingMin === null
                ? ""
                : t.canDrive}
          </span>
        </span>
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-180",
          )}
        />
      </button>
      {open && <Details row={row} />}
    </div>
  )
}

function newest(rows: TachoCardRow[]) {
  return (
    rows
      .map((r) => r.sourceAt)
      .filter((t): t is string => !!t)
      .sort()
      .at(-1) ?? null
  )
}

export function TachoFleetCard({
  rows,
  now,
}: {
  rows: TachoCardRow[]
  now: number
}) {
  const t = useTachoCardStrings()
  const [mode, setMode] = useState<Mode>("moving")
  const [all, setAll] = useState(false)
  const lists = { moving: movingRows(rows), standing: standingRows(rows) }
  const tabs: Array<[Mode, string]> = [
    ["moving", t.movingTab(lists.moving.length)],
    ["standing", t.standingTab(lists.standing.length)],
  ]
  const list = lists[mode]
  const shown = all ? list : list.slice(0, SHOWN)
  return (
    <div
      data-testid="tacho-fleet-card"
      className="mt-3 rounded-xl border border-border bg-card px-4 pb-3 pt-3"
    >
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-semibold">{t.title(rows.length)}</div>
        <div className="inline-flex rounded-full bg-muted p-0.5">
          {tabs.map(([id, text]) => (
            <button
              key={id}
              type="button"
              onClick={() => {
                setMode(id)
                setAll(false)
              }}
              className={cn(
                "rounded-full px-3 py-1 text-xs font-medium",
                mode === id
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground",
              )}
            >
              {text}
            </button>
          ))}
        </div>
      </div>
      {list.length === 0 && (
        <div className="border-t border-border py-3 text-sm text-muted-foreground">
          {mode === "moving" ? t.noneMoving : t.noneStanding}
        </div>
      )}
      {shown.map((row, i) => (
        <TachoRow key={`${row.plate}-${i}`} row={row} mode={mode} />
      ))}
      {list.length > shown.length && (
        <button
          type="button"
          onClick={() => setAll(true)}
          className="w-full border-t border-border py-2.5 text-sm text-primary"
        >
          {t.showAll(list.length)}
        </button>
      )}
      <TachoSourceFooter sourceAt={newest(rows)} now={now} />
    </div>
  )
}
